import React, { useCallback, useMemo, useState } from 'react';
import { InfoCard } from 'app/shared/components/generic-ui/InfoCard/InfoCard';
import { INotificationEntityCard, NotificationEntityType, Routes } from 'types';
import { NotificationEntityCardTeamSection } from './NotificationEntityCardTeamSection';
import { generatePath, useNavigate } from 'react-router';
import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import { useNotificationSettingsEntityApi } from 'app/shared/hooks';
import { firebaseAnalytics } from 'app/shared/analytics';
import { StatusSection } from './StatusSection/StatusSection';
import { NotificationEntityCardInfoSection } from './NotificationEntityCardInfoSection/NotificationEntityCardInfoSection';
import { CustomTeamModal } from 'app/shared/components/generic-ui';
import { CustomTypography } from 'app/shared/components/generic-ui/Typography/Typography';
import { getNotificationEntityName } from 'app/shared/utils';
import { translations } from 'app/locales/i18n';
import project_id_icon from 'assets/project_id_icon.svg';
import users from 'assets/users.svg';

import calendarIcon from 'assets/calendar.svg';

import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ProjectFavoriteButton } from '../ProjectFavoriteButton/ProjectFavoriteButton';

interface INotificationEntityCardCardProps {
  notificationEntityCard: INotificationEntityCard;
  isUpcoming?: boolean;
  setUpcomingNotificationCard?: (
    notificationEntityCard: INotificationEntityCard,
  ) => void;
}

export const NotificationEntityCardCard = ({
  notificationEntityCard,
  isUpcoming,
  setUpcomingNotificationCard,
}: INotificationEntityCardCardProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const {
    organizationId,
    entityType,
  }: { organizationId: string; entityType: string } = useRouteParams();
  const entityName = getNotificationEntityName(Number(entityType));

  // Vehicles are looked up by their ID (truck number) rather than by name,
  // so the ID becomes the card title and the name moves to the info section.
  const isVehicle = Number(entityType) === NotificationEntityType.VEHICLE;

  const cardTitle = isVehicle
    ? notificationEntityCard?.externalId || '-'
    : notificationEntityCard?.name || '';

  const infoSectionValue = isVehicle
    ? notificationEntityCard.name
    : notificationEntityCard.externalId;

  const infoSectionLabel = isVehicle
    ? t(translations.notificationEntityCardCard.entityName, {
        entity: entityName,
      })
    : t(translations.notificationEntityCardCard.entityID, {
        entity: entityName,
      });

  const formattedLastDispatchDate = useMemo(
    () =>
      notificationEntityCard.lastTicketDispatchDateTimeUtc
        ? format(
            new Date(notificationEntityCard.lastTicketDispatchDateTimeUtc),
            'MMM d, y - hh:mm bb',
          )
        : undefined,
    [notificationEntityCard.lastTicketDispatchDateTimeUtc],
  );
  const formattedNextDispatchDate = useMemo(
    () =>
      notificationEntityCard.nextTicketDispatchDateTimeUtc
        ? format(
            new Date(notificationEntityCard.nextTicketDispatchDateTimeUtc),
            'MMM d, y - hh:mm bb',
          )
        : undefined,
    [notificationEntityCard.nextTicketDispatchDateTimeUtc],
  );
  const notificationEntityMembersQueryParams = useMemo(() => {
    return {
      organizationId: organizationId,
      entityType: Number(entityType),
      queryParameters: {
        sortBy: 'firstname,lastname',
        sortAsc: true,
        notificationEntityId: notificationEntityCard.internalId,
      },
    };
  }, [organizationId, entityType, notificationEntityCard.internalId]);

  const {
    fetchActiveEntityAssignments: fetchNotificationEntityMembers,
    isFetchingActiveEntityAssignments: isFetchingProjectMembers,
    activeEntityAssignments: projectMembers,
  } = useNotificationSettingsEntityApi(notificationEntityMembersQueryParams);

  const onClickMembers = useCallback(async () => {
    setIsTeamModalOpen(true);
    await fetchNotificationEntityMembers();

    firebaseAnalytics.logPressEvent(
      Routes.NotificationEntityPage,
      `${entityName} card members`,
    );
  }, [fetchNotificationEntityMembers, entityName]);

  const handleCardClick = useCallback(() => {
    firebaseAnalytics.logPressEvent(
      Routes.NotificationEntityPage,
      `${entityName} card`,
    );

    if (
      isUpcoming &&
      Number(entityType) === NotificationEntityType.POUR_EVENT
    ) {
      setUpcomingNotificationCard?.(notificationEntityCard);
      return;
    }

    navigate(
      `${generatePath(Routes.NotificationEntityPourEventsPage, {
        entityType,
        notificationEntityId: notificationEntityCard.internalId,
        organizationId,
        page: '1',
        perPage: '20',
        name: encodeURIComponent(notificationEntityCard.name),
        externalId: notificationEntityCard.externalId,
      })}?upcoming=${isUpcoming ? 'true' : 'false'}`,
    );
  }, [
    entityName,
    isUpcoming,
    entityType,
    navigate,
    notificationEntityCard,
    organizationId,
    setUpcomingNotificationCard,
  ]);

  return (
    <>
      {isTeamModalOpen && (
        <CustomTeamModal
          isOpen={isTeamModalOpen}
          closeHandler={() => {
            setIsTeamModalOpen(false);
          }}
          team={projectMembers}
          isLoading={isFetchingProjectMembers}
        />
      )}

      <InfoCard onClick={handleCardClick}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              flex: 1,
              minWidth: 0,
            }}
          >
            <CustomTypography variant="header5" bold color="accentDark">
              {cardTitle}
            </CustomTypography>
            <ProjectFavoriteButton
              internalId={notificationEntityCard.internalId}
            />
          </div>
          <StatusSection notificationEntityBaseModel={notificationEntityCard} />
        </div>
        <div>
          <NotificationEntityCardInfoSection
            value={infoSectionValue}
            label={infoSectionLabel}
            icon={project_id_icon}
          />
        </div>
        <div>
          <NotificationEntityCardTeamSection
            teammatesCount={
              notificationEntityCard?.notificationEntitySummaryModel
                .assignedUsersCount
            }
            showTeamModal={onClickMembers}
          />
        </div>
        {notificationEntityCard.notificationEntityGroupName ? (
          <div>
            <NotificationEntityCardInfoSection
              value={notificationEntityCard.notificationEntityGroupName}
              label={t(translations.notificationEntityCardCard.group)}
              icon={users}
            />
          </div>
        ) : (
          <div />
        )}
        {formattedLastDispatchDate ? (
          <div>
            <NotificationEntityCardInfoSection
              value={formattedLastDispatchDate}
              label={t(translations.notificationEntityCardCard.lastDispatch)}
              icon={calendarIcon}
            />
          </div>
        ) : (
          <div />
        )}
        {formattedNextDispatchDate ? (
          <div>
            <NotificationEntityCardInfoSection
              value={formattedNextDispatchDate}
              label={t(translations.notificationEntityCardCard.nextDispatch)}
              icon={calendarIcon}
            />
          </div>
        ) : (
          <div />
        )}
      </InfoCard>
    </>
  );
};
